import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { USAGE_TYPES } from './categories';
import ProjectImport from './ProjectImport';

interface Project {
  id: string;
  name: string;
  street: string;
  city: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  usageType: string;
  usageSubType: string;
  owner: string;
  description: string;
  customerId: string | null;
  customer?: { id: string; name: string };
  plots: { buildings: any[] }[];
  createdAt: string;
}

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [showImport, setShowImport] = useState(false);

  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [usageType, setUsageType] = useState('');
  const [usageSubType, setUsageSubType] = useState('');
  const [owner, setOwner] = useState('');
  const [description, setDescription] = useState('');

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isPointreef = ['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role);
  const canWrite = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN', 'CUSTOMER_MANAGER'].includes(user.role);
  const subTypes = usageType ? (USAGE_TYPES[usageType] || []) : [];

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProjects();
    if (canWrite) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => setCustomers(r.data)).catch(console.error);
    }
  }, []); // eslint-disable-line

  const geocodeAddress = async (street: string, city: string, zipCode: string) => {
    try {
      setGeocoding(true);
      const query = `${street}, ${zipCode} ${city}, Germany`;
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        { headers: { 'Accept-Language': 'de' } }
      );
      const data = await res.json();
      if (data.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch (err) {
      console.error(err);
    } finally {
      setGeocoding(false);
    }
    return null;
  };

  const resetForm = () => {
    setName(''); setStreet(''); setCity(''); setZipCode('');
    setUsageType(''); setUsageSubType(''); setOwner(''); setDescription('');
    setSelectedCustomerId('');
  };

  const openCreateForm = () => {
    setEditProject(null);
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (project: Project) => {
    setEditProject(project);
    setName(project.name);
    setStreet(project.street || '');
    setCity(project.city || '');
    setZipCode(project.zipCode || '');
    setUsageType(project.usageType || '');
    setUsageSubType(project.usageSubType || '');
    setOwner(project.owner || '');
    setDescription(project.description || '');
    setSelectedCustomerId(project.customerId || ''); // ← Fix: Kunde vorbelegen
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name || !street || !city) return;
    setLoading(true);
    try {
      if (editProject) {
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/projects/${editProject.id}`, {
          name, street, city, zipCode, usageType, usageSubType, owner, description,
          customerId: selectedCustomerId || null
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const coords = await geocodeAddress(street, city, zipCode);
        await axios.post(`${process.env.REACT_APP_API_URL}/api/projects`, {
          name, street, city, zipCode,
          latitude: coords?.lat, longitude: coords?.lng,
          usageType, usageSubType, owner, description,
          customerId: selectedCustomerId || null
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowForm(false);
      setEditProject(null);
      resetForm();
      fetchProjects();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/projects/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirm(null);
      fetchProjects();
    } catch (err) {
      console.error(err);
    }
  };

  const countBuildings = (project: Project) =>
    project.plots?.reduce((sum, plot) => sum + (plot.buildings?.length || 0), 0) || 0;

  const getUsageLabel = (project: Project) => {
    if (project.usageSubType) return `${project.usageType} · ${project.usageSubType}`;
    return project.usageType;
  };

  return (
    <div style={s.container}>
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a href="/dashboard" style={s.navItem}>Startseite</a>
          <a style={s.navItemActive}>Liegenschaften</a>
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN'].includes(user.role) && (
            <a href="/users" style={s.navItem}>Benutzer</a>
          )}
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role) && (
            <a href="/customers" style={s.navItem}>Kunden</a>
          )}
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{user.firstName} {user.lastName}</p>
          <p style={s.userRole}>{user.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>Abmelden</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Liegenschaften</h1>
            <p style={s.subtitle}>{projects.length} Liegenschaften</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {canWrite && (
              <button style={s.importButton} onClick={() => setShowImport(true)}>
                Importieren
              </button>
            )}
            {canWrite && (
              <button style={s.addButton} onClick={openCreateForm}>+ Neue Liegenschaft</button>
            )}
          </div>
        </div>

        {showForm && (
          <div style={s.form}>
            <h3 style={s.formTitle}>{editProject ? 'Liegenschaft bearbeiten' : 'Neue Liegenschaft anlegen'}</h3>
            <div style={s.formGrid}>
              <div style={s.inputGroup}>
                <label style={s.label}>Name *</label>
                <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="z.B. Gesamtschule Duisburg Nord" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Eigentümer</label>
                <input style={s.input} value={owner} onChange={e => setOwner(e.target.value)} placeholder="z.B. Stadt Duisburg" />
              </div>
              {isPointreef && customers.length > 0 && (
                <div style={s.inputGroup}>
                  <label style={s.label}>Kunde</label>
                  <select style={s.input} value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
                    <option value="">Bitte wählen...</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div style={s.inputGroup}>
                <label style={s.label}>Straße & Hausnummer *</label>
                <input style={s.input} value={street} onChange={e => setStreet(e.target.value)} placeholder="z.B. Falkstraße 44" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>PLZ</label>
                <input style={s.input} value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="z.B. 47058" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Stadt *</label>
                <input style={s.input} value={city} onChange={e => setCity(e.target.value)} placeholder="z.B. Duisburg" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Nutzungsart</label>
                <select style={s.input} value={usageType} onChange={e => { setUsageType(e.target.value); setUsageSubType(''); }}>
                  <option value="">Bitte wählen...</option>
                  {Object.keys(USAGE_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {subTypes.length > 0 && (
                <div style={s.inputGroup}>
                  <label style={s.label}>Unterkategorie <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span></label>
                  <select style={s.input} value={usageSubType} onChange={e => setUsageSubType(e.target.value)}>
                    <option value="">Bitte wählen...</option>
                    {subTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              <div style={{ ...s.inputGroup, gridColumn: '1 / -1' }}>
                <label style={s.label}>Beschreibung</label>
                <textarea style={{ ...s.input, height: '80px', resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optionale Beschreibung..." />
              </div>
            </div>
            {!editProject && (
              <p style={{ color: '#888', fontSize: '12px', margin: '8px 0 0 0' }}>
                * Pflichtfelder · Koordinaten werden automatisch per Geocoding ermittelt
              </p>
            )}
            <div style={s.formButtons}>
              <button style={s.cancelButton} onClick={() => { setShowForm(false); setEditProject(null); resetForm(); }}>Abbrechen</button>
              <button style={{ ...s.saveButton, opacity: loading || geocoding ? 0.7 : 1 }} onClick={handleSave} disabled={loading || geocoding}>
                {geocoding ? 'Adresse wird ermittelt...' : loading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        <div style={s.grid}>
          {projects.map((project) => (
            <div key={project.id} style={s.card}>
              <div style={s.cardHeader}>
                <div style={{ flex: 1 }}>
                  <h3 style={s.cardTitle}>{project.name}</h3>
                  {project.usageType && <span style={s.badge}>{getUsageLabel(project)}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={s.buildingCount}>{countBuildings(project)} Gebäude</span>
                  {canWrite && (
                    <>
                      <button style={s.iconBtn} title="Bearbeiten" onClick={() => openEditForm(project)}>✏️</button>
                      <button style={s.iconBtn} title="Löschen" onClick={() => setDeleteConfirm(project)}>🗑️</button>
                    </>
                  )}
                </div>
              </div>
              <div style={s.cardAddress}>
                <p style={s.addressLine}>📍 {project.street}</p>
                <p style={s.addressLine}>{project.zipCode} {project.city}</p>
              </div>
              {project.owner && <p style={s.owner}>🏛️ {project.owner}</p>}
              {project.customer && <p style={s.owner}>👥 {project.customer.name}</p>}
              {project.description && <p style={s.cardDesc}>{project.description}</p>}
              <div style={s.cardFooter}>
                <span style={s.plotCount}>{project.plots?.length || 0} Grundstück(e)</span>
                <button style={s.openButton} onClick={() => window.location.href = `/projects/${project.id}`}>Öffnen →</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {deleteConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={s.modalIcon}>🗑️</p>
            <p style={s.modalTitle}>Liegenschaft löschen</p>
            <p style={s.modalText}>
              Möchtest du <strong>{deleteConfirm.name}</strong> wirklich löschen?
              Alle zugehörigen Grundstücke, Gebäude und Dateien werden ebenfalls gelöscht.
            </p>
            <div style={s.modalButtons}>
              <button style={s.cancelButton} onClick={() => setDeleteConfirm(null)}>Abbrechen</button>
              <button style={s.deleteButton} onClick={handleDelete}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <ProjectImport
          customers={customers}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchProjects(); }}
        />
      )}
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '32px', overflow: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', color: '#0f2744', margin: '0' },
  subtitle: { color: '#888', margin: '4px 0 0 0' },
  addButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  importButton: { backgroundColor: 'transparent', border: '1px solid #00c49a', color: '#00c49a', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', fontSize: '14px' },
  form: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  formTitle: { margin: '0 0 20px 0', color: '#0f2744' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#333' },
  input: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelButton: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  saveButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton: { backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderTop: '4px solid #00c49a' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '8px' },
  cardTitle: { margin: '0 0 4px 0', color: '#0f2744', fontSize: '16px' },
  badge: { backgroundColor: '#f0f4ff', color: '#3498db', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  buildingCount: { backgroundColor: '#f0faf7', color: '#00c49a', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '2px 4px', borderRadius: '4px' },
  cardAddress: { marginBottom: '8px' },
  addressLine: { color: '#555', fontSize: '13px', margin: '0 0 2px 0' },
  owner: { color: '#888', fontSize: '12px', margin: '0 0 8px 0' },
  cardDesc: { color: '#888', fontSize: '13px', margin: '0 0 12px 0' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' },
  plotCount: { color: '#aaa', fontSize: '12px' },
  openButton: { backgroundColor: 'transparent', border: '1px solid #00c49a', color: '#00c49a', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center' },
  modalIcon: { fontSize: '32px', margin: '0 0 8px 0' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' },
  modalText: { color: '#666', fontSize: '14px', margin: '0 0 24px 0', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
};

export default Projects;
